import { z } from 'zod';
import { MoneySchema } from './common.js';
/**
 * Slip verification wire shapes (SAD §6, M-04).
 *
 * `VerifiedSlip` is the canonical, provider-normalized result — the *only* shape any provider
 * adapter is allowed to emit. `VerificationDecision` is the pure, total decision produced by
 * `decide(slip, expected, policy)` (M-04): exactly one of the SAD §6 decision-matrix outcomes.
 */
// ---------------------------------------------------------------------------------------------
// VerifiedSlip (SAD §6)
// ---------------------------------------------------------------------------------------------
/**
 * Canonical `VerifiedSlip`, normalized from any provider (SAD §6):
 *
 * ```
 * { "provider": "slipok", "trans_ref": "01420064...",
 *   "receiver": { "name": "...", "bank": "KBANK", "account_masked": "xxx-x-x5678-x" },
 *   "amount": { "value": "500.00", "currency": "THB" },
 *   "transferred_at": "2026-07-23T10:05:03+07:00" }
 * ```
 */
export const VerifiedSlipSchema = z
    .object({
    provider: z.string().min(1),
    /** Bank transaction ref — the anti-duplicate key (SAD §6). Always present on success. */
    trans_ref: z.string().min(1),
    receiver: z
        .object({
        name: z.string().min(1),
        bank: z.string().min(1),
        /**
         * Often masked, and often omitted entirely, by the provider.
         *
         * Optional by design. SAD §6 lists this field as required-to-auto-approve only
         * "if provided", and its provider-limitation note is explicit: "Often masked/omitted →
         * match last-4 when present; don't require."
         *
         * Modelling it as required forces an adapter to invent a sentinel value for the omitted
         * case — which is a magic value that every consumer then has to know about. `undefined`
         * is the honest representation of "the bank did not tell us", and the domain's last-4
         * extraction already reads that as `ABSENT` rather than as a mismatch.
         */
        account_masked: z.string().min(1).optional(),
    })
        .strict(),
    amount: MoneySchema,
    transferred_at: z.string().datetime({ offset: true }),
    /**
     * The sending party, when the provider discloses it — verify-the-sender (SAD §6 extension).
     *
     * Every field independently optional, mirroring `receiver.account_masked`'s rationale: real
     * providers routinely omit sender detail entirely, or disclose only part of it. The whole
     * object is optional too, for providers that omit sender information altogether.
     */
    sender: z
        .object({
        name: z.string().min(1).optional(),
        bank: z.string().min(1).optional(),
        /**
         * The bank's mask for the sending account, verbatim (`xxx-x-x2901-x`). The field to
         * compare on: every bank hides a different span, so the only sound comparison is
         * positional against the full registered number (`@pf/domain`'s `matchCounterparty`).
         */
        account_masked: z.string().min(1).optional(),
        /**
         * The account's genuine trailing four digits — present ONLY when the mask actually ended
         * in four visible digits, which is a different claim from "the last four digits the bank
         * happened to print". A mask showing digits 6–9 says nothing about digits 7–10, so
         * adapters must derive this with `trailingLast4FromMask` and omit it otherwise. Kept for
         * the providers that disclose a bare tail with no mask around it.
         */
        account_last4: z.string().length(4).optional(),
    })
        .strict()
        .optional(),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// Check matrix (SAD §6 required verified data; SAD §8 GET /v1/transactions/{id} example)
// ---------------------------------------------------------------------------------------------
/** Outcome of a single verification check. */
export const CheckResultSchema = z.enum(['PASS', 'FAIL', 'ABSENT']);
/** The four checks SAD §6 requires before auto-approval, and that GET /v1/transactions echoes. */
export const CheckMatrixSchema = z
    .object({
    name: CheckResultSchema,
    amount: CheckResultSchema,
    bank: CheckResultSchema,
    duplicate: CheckResultSchema,
    /**
     * Whether the slip's sender matches the reservation's `payer_account`, when one was supplied.
     *
     * Omitted entirely — not `ABSENT` — when the reservation carried no `payer_account`: the
     * check did not apply, and legacy rows/consumers that predate it must see exactly the
     * four-field matrix they always have.
     */
    sender: CheckResultSchema.optional(),
    /**
     * Receiver-account last-4 and slip-timestamp freshness. The engine has always computed and
     * stored these in the persisted matrix; exposing them lets a participant's UI explain *why*
     * a slip went to review instead of guessing from the four basics. Optional for the same
     * legacy-row reason as `sender`.
     */
    account: CheckResultSchema.optional(),
    freshness: CheckResultSchema.optional(),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// VerificationDecision — the SAD §6 decision matrix (M-04)
// ---------------------------------------------------------------------------------------------
/**
 * An auto-reconciled difference between the slip's amount and the reserved amount
 * (`PolicyConfig.amountAutoReconcileUnder` / `…Over`). Semantics are fixed by `direction`:
 *
 *  - `UNDER` — credit the depositor `paid_amount`; commit `paid_amount` of the withdrawer's
 *    funds lock and release `delta` back to their available balance.
 *  - `OVER` — credit the depositor `reserved_amount` plus up to `delta` more, capped by the
 *    withdrawer's available balance at apply time; debit the withdrawer exactly that extra and
 *    commit the full lock. The website reports what it actually moved on
 *    `credit-result.settlement`.
 *
 * `delta` is always `|paid_amount − reserved_amount|`, strictly positive.
 */
export const AmountAdjustmentSchema = z
    .object({
    direction: z.enum(['UNDER', 'OVER']),
    paid_amount: MoneySchema,
    reserved_amount: MoneySchema,
    delta: MoneySchema,
})
    .strict();
/**
 * What a website's ledger actually moved when it applied a credit that carried an
 * {@link AmountAdjustmentSchema}. Reported on `POST /v1/transactions/{id}/credit-result` and
 * echoed on `transaction.completed` / `withdrawal.fulfilled` for the audit read.
 */
export const CreditSettlementSchema = z
    .object({
    /** What the depositor's balance actually gained. */
    credited_amount: MoneySchema,
    /** UNDER only: the slice of the funds lock returned to the withdrawer's available balance. */
    released_amount: MoneySchema.optional(),
    /** OVER only: the extra taken from the withdrawer's available balance, `≤ delta`. */
    extra_debited_amount: MoneySchema.optional(),
})
    .strict();
/**
 * `trans_ref` unseen AND amount exact AND bank exact AND name ≥ 0.90 AND (last-4 matches or
 * not provided). The only outcome that may move a transaction to CONFIRMED (P4).
 *
 * `adjustment` is the one policy-sanctioned exception to "amount exact": present when the
 * amount check failed but `amountAutoReconcileUnder`/`…Over` settled the difference instead of
 * reviewing it.
 */
export const AutoApproveDecisionSchema = z
    .object({
    kind: z.literal('AUTO_APPROVE'),
    checks: CheckMatrixSchema,
    adjustment: AmountAdjustmentSchema.optional(),
})
    .strict();
/**
 * Fraud-class only: the payment claim itself is discredited, so the slot may re-pool (SAD §6).
 */
export const RejectDecisionSchema = z
    .object({
    kind: z.literal('REJECT'),
    reason: z.enum(['DUPLICATE_TRANS_REF', 'NOT_A_SLIP']),
    checks: CheckMatrixSchema,
})
    .strict();
/**
 * Wrong-slip mismatch. A wrong file does not prove no payment (P5) — the withdrawal stays
 * LOCKED and the depositor may re-submit, up to 3 attempts within the evidence deadline.
 */
export const ReSubmitDecisionSchema = z
    .object({
    kind: z.literal('RE_SUBMIT'),
    reason: z.enum(['BANK_MISMATCH', 'ACCOUNT_MISMATCH', 'NAME_BELOW_RESUBMIT']),
})
    .strict();
/** Ambiguous — never auto-approve unverified (P4); an operator resolves it. */
export const ReviewDecisionSchema = z
    .object({
    kind: z.literal('REVIEW'),
    reason: z.enum([
        'NAME_BAND',
        'AMOUNT_MISMATCH',
        'INCOMPLETE_DATA',
        'STALE_TIMESTAMP',
        'ATTEMPTS_EXHAUSTED',
        /** The slip's sender did not match the reservation's `payer_account` (verify-the-sender). */
        'SENDER_MISMATCH',
        /**
         * Cross-participant match only: the slip's sender looks like the expected payee, so the
         * depositor may be settling their own withdrawal on another website.
         */
        'SELF_MATCH_SUSPECTED',
        'SLIP_ALREADY_CLAIMED',
        /** A duplicate `trans_ref` handed to a human — `duplicate_trans_ref_auto_reject` is off. */
        'DUPLICATE_SLIP',
    ]),
})
    .strict();
/** Transient provider fault — backoff, idempotent by slip hash; exhaustion escalates to REVIEW. */
export const RetryDecisionSchema = z
    .object({
    kind: z.literal('RETRY'),
    reason: z.enum(['PROVIDER_TIMEOUT', 'PROVIDER_5XX', 'CIRCUIT_OPEN', 'SLIP_NOT_FOUND']),
})
    .strict();
/**
 * The exhaustive, pure result of `decide(slip, expected, policy)` (M-04) — one of the five SAD
 * §6 decision-matrix outcomes. Discriminated on `kind`.
 */
export const VerificationDecisionSchema = z.discriminatedUnion('kind', [
    AutoApproveDecisionSchema,
    RejectDecisionSchema,
    ReSubmitDecisionSchema,
    ReviewDecisionSchema,
    RetryDecisionSchema,
]);
//# sourceMappingURL=verification.js.map